import { useEffect, useState } from 'react';

import { Button } from '@/components/Button';
import { useAuth } from '@/store/auth/auth.store';

import OtpInput from '../OtpCode/OtpInput';

import RecoveryCodes from './RecoveryCodes';
import s from './TotpEnroll.module.scss';

/** How long a fresh code has before another can be requested. */
const RESEND_SECONDS = 30;

type CodeConfirmProps = {
  /** The channel the code was sent over. */
  method: 'email' | 'sms';
  /** Where the code went; named in the subtitle when given. */
  destination?: string;
  /**
   * Checks the code. Defaults to confirming the pending 2FA `method`, which resolves to
   * the account's recovery codes (empty unless it is the first confirmed method).
   */
  verify?: (code: string) => Promise<string[] | void>;
  /** Sends a fresh code. Defaults to re-arming the 2FA `method`. */
  resend?: () => Promise<void>;
  /** Turns a rejected `verify` into the text shown under the code. */
  errorText?: (err: unknown) => string;
  /**
   * Fired once the code is accepted, before any recovery codes are shown. Use it to
   * refresh status or toast. Receives the recovery codes (empty unless this is the
   * account's first confirmed method).
   */
  onConfirmed?: (recoveryCodes: string[]) => void | Promise<void>;
  /** Fired when finished — immediately when no recovery codes, else after they ack. */
  onDone: () => void;
};

/**
 * Enter-the-emailed-or-texted-code step. By default it confirms a code-based 2FA method
 * (email / SMS): enabling one only arms it server-side and sends a one-time code, and it
 * stays pending until this screen proves receipt. Mirrors {@link TotpEnroll} but with no
 * QR — just the code. `verify` and `resend` point it at any other code, like the one
 * that confirms a login change.
 */
const CodeConfirm = ({
  method,
  destination,
  verify,
  resend,
  errorText,
  onConfirmed,
  onDone,
}: CodeConfirmProps) => {
  const [code, setCode] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // A code has just gone out when this screen opens, so the wait starts right away.
  const [secondsLeft, setSecondsLeft] = useState(RESEND_SECONDS);
  const [sending, setSending] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);

  const ready = code.join('').length === 6;

  useEffect(() => {
    if (secondsLeft <= 0) return;
    const id = setTimeout(() => setSecondsLeft((n) => n - 1), 1000);
    return () => clearTimeout(id);
  }, [secondsLeft]);

  const confirm = async () => {
    if (!ready || busy) return;
    setBusy(true);
    setError('');
    try {
      const codes = verify
        ? ((await verify(code.join(''))) ?? [])
        : await useAuth.getState().confirmTwoFactorMethod(method, code.join(''));
      await onConfirmed?.(codes);
      if (codes.length)
        setRecoveryCodes(codes); // show once, then onDone
      else onDone();
    } catch (err) {
      console.error('Failed to confirm the code:', err);
      setError(errorText?.(err) ?? 'That code didn’t match. Please check it and try again.');
    } finally {
      setBusy(false);
    }
  };

  // Send a fresh code (the previous one expires).
  const sendAgain = async () => {
    if (secondsLeft > 0 || sending) return;
    setSending(true);
    try {
      await (resend ? resend() : useAuth.getState().enableTwoFactorMethod(method));
      setCode([]);
      setError('');
      setSecondsLeft(RESEND_SECONDS);
    } catch (err) {
      console.error('Failed to resend the code:', err);
      setError('Couldn’t send a new code. Please try again.');
    } finally {
      setSending(false);
    }
  };

  if (recoveryCodes) {
    return <RecoveryCodes codes={recoveryCodes} onDone={onDone} />;
  }

  return (
    <div className={s.body}>
      <div className={s.heading}>
        <h2 className={s.title}>Enter Code</h2>
        <p className={s.subtitle}>
          {destination
            ? `We sent a 6-digit code to ${destination}.`
            : method === 'sms'
              ? 'We sent a 6-digit code by text message.'
              : 'We sent a 6-digit code to your email.'}
        </p>
      </div>
      <OtpInput value={code} onChange={setCode} onUserInput={() => setError('')} autoFocus />
      {error && <p className={s.error}>{error}</p>}
      <div className={s.resend}>
        {secondsLeft > 0 ? (
          <span className={s.resendTimer}>Resend in 0:{String(secondsLeft).padStart(2, '0')}</span>
        ) : (
          <button className={s.resendBtn} onClick={sendAgain} type="button" disabled={sending}>
            {sending ? 'Sending…' : 'Resend Code'}
          </button>
        )}
      </div>
      <Button className={s.submit} onClick={confirm} disabled={!ready || busy}>
        {busy ? 'Verifying…' : 'Confirm'}
      </Button>
    </div>
  );
};

export default CodeConfirm;
